import { Auth, Storage } from 'aws-amplify';
import API from '@aws-amplify/api';
import {
    editUserInfo,
    createProject,
    updateUser,
    addCustomerUserToProject,
    forgotPassword,
    restoreUser,
    editUserResource
} from '../graphql/mutations';
import Constant from '@/common/constant/Constant';
import {
    getUserList,
} from '../graphql/queries';
import Utils from '@/common/utils/utils';
import Logger from '@/common/utils/Logger';
import crypto from 'crypto-js';
import localStorageUtil from '@/common/utils/localStorageUtil';
import sessionStorageUtil from '@/common/utils/sessionStorageUtil';
import { getCorporationByCorporationId, getCorporationById } from './organizationService';
import { cloneDeep } from 'lodash';

import * as queries from '../graphql/queries';
import * as DynamoDBUserRequestDto from './dynamoDBRequestDto/DynamoDBUserRequestDto';
import * as DynamoDBProjectRequestDto from './dynamoDBRequestDto/DynamoDBProjectRequestDto';
import { getProjectById, getProjectIdListUserIsMember } from '@/service/projectService';
import { pushClientLog } from '@/service/logService';

/**
 * formatUserToDTO
 * Format user data to match UserDTO structure from backend
 * @param {*} user 
 * @returns UserDTO object
 */
export const formatUserToDTO = (user) => {
    const fullName = Utils.getFullNameOfUser(Constant.LANGUAGE_TYPE.JP, user);
    const orgName = user.organizationName || '';
    const displayName = [
        fullName,
        orgName,
        user.tel ? `+${user.tel}` : null
    ].filter(Boolean).join(' ');
    return {
        id: user.id,
        displayName: displayName,
        image: user.avatar || user.image || null,
        tel: user.tel ? (user.tel.startsWith('+') ? user.tel : `+${user.tel}`) : null,
    };
};

/**
 * updateB2CUserListInAtom
 * Compare and update B2C user list atom if there is any change
 * @param {*} currentAtomList - Current list B2C user list in org atom
 * @param {*} changedUsers - List of users that have been changed (add/update)
 * @param {*} deletedUserIds - List of user IDs that have been deleted
 * @param {*} setAtom - Function to set list B2C user list in org atom
 */
export const updateB2CUserListInAtom = (currentAtomList, changedUsers, deletedUserIds, setAtom) => {
    if (!setAtom) return;
    
    let newList = cloneDeep(currentAtomList);
    
    // Remove deleted users
    if (deletedUserIds && deletedUserIds.length > 0) {
        newList = newList.filter(user => !deletedUserIds.includes(user.id));
    }
    
    // Add or update changed users
    if (changedUsers && changedUsers.length > 0) {
        changedUsers.forEach(changedUser => {
            const userDTO = formatUserToDTO(changedUser);
            const existingIndex = newList.findIndex(u => u.id === changedUser.id);
            
            if (existingIndex !== -1) {
                // Update existing user
                newList[existingIndex] = userDTO;
            } else {
                // Add new user
                newList.push(userDTO);
            }
        });
    }
    
    // Only update atom if there's actual change
    if (JSON.stringify(newList) !== JSON.stringify(currentAtomList)) {
        setAtom(newList);
    }
};

export const getUserPermissionToViewKnowledge = async (corporationDetail, projectInuseDetail, userId) => {
    if (!corporationDetail || Utils.isEmptyObject(corporationDetail)) {
        const corporationId = localStorageUtil.getAffiliationOrganization();
        if (corporationId) {
            corporationDetail = await getCorporationById(API, corporationId);
        }
    }

    if (!projectInuseDetail || Utils.isEmptyObject(projectInuseDetail)) {
        const projectInuseId = localStorageUtil.getSelectedProjectId();
        if (projectInuseId) {
            projectInuseDetail = await getProjectById(API, projectInuseId);
        }
    }

    if (!userId) {
        userId = localStorageUtil.getUserDetailId();
    }

    const userPermissionType = Utils.checkUserPermissionKnowledgeService(
        corporationDetail,
        projectInuseDetail
    );

    let knowledgeService = [
        Constant.CORPORATION_CONTRACT_TYPE.GUEST,
        Constant.CORPORATION_CONTRACT_TYPE.GUEST_DESK
    ].includes(corporationDetail.contractType)
        ? Utils.convertJSONDataToObject(corporationDetail?.inviterSetting)?.usingKnowledgeService
        : corporationDetail.knowledgeService;
    return {
        type: userPermissionType,
        knowledgeService: knowledgeService,
        projectSelectedId: projectInuseDetail.id,
        corporationSelectedId: corporationDetail.id,
        userId: userId
    };
}

export const getUserAvatarUrl = (keyFileAvatar) => {
    if (Utils.isEmptyString(keyFileAvatar)) {
        return Constant.AVATAR_DEFAULT;
    }

    const expiryTime = new Date().getTime() + Constant.S3_URL_EXPIRY_TIME * 60000;
    let paramsUrlObj = {
        type: Constant.S3_FILE_TYPE.USER_PROFILE_FILE,
        expiryTime: expiryTime,
        keyFile: `/public/${keyFileAvatar}`,
        publicAll: false,
        publicAuthenticated: true
    };
    const paramsUrl = encodeURIComponent(
        crypto.AES.encrypt(JSON.stringify(paramsUrlObj), Constant.S3_URL_AES_ENCODE_KEY).toString()
    );
    return `${process.env.REACT_APP_CLOUD_FRONT_URL}/${paramsUrl}`;
};

/**
 * Get user detail in corporation by userId
 * @param {*} APIClient
 * @param {*} corporationId
 * @param {*} userId
 * @returns userDetail
 */
export const getUserDetailInCorporationByUserId = async (APIClient, corporationId, userId) => {
    try {
        const userDetail = await getUserByUserIdAndCorporationId(API, userId, corporationId);
        if (!userDetail) {
            return null;
        }
        // call s3 get avatar url
        let avatar = Constant.AVATAR_DEFAULT;
        if (!Utils.isEmptyString(userDetail.image)) {
            avatar = getUserAvatarUrl(userDetail.image);
        }
        userDetail.avatar = avatar;
        return userDetail;
    } catch (err) {
        Logger.error('getUserDetailInCorporationByUserId error: ', err);
        return null;
    }
};


/**
 * 
 * @param {*} APIClient 
 * @param {*} id 
 * @returns userDetail
 */
export const getUserById = async (APIClient, id) => {
    let userDetail = {};
    if (Utils.getPrefixOfId(id) !== 'user') return userDetail;
    try {
        const res = await API.graphql({
            query: queries.getUserDetail,
            variables: {
                params: {
                    id: id
                }
            }
        });

        userDetail = !Utils.isEmptyObject(res.data.getUserDetail) ? res.data.getUserDetail : null;
    } catch (err) {
        console.log('getUserById error: ', err);
    }
    return userDetail;
};

export const isLimitedTabsLength = async ({ userDetail, projectInuseDetail, panelDataChatAI }) => {
    if (panelDataChatAI?.tabList?.length >= Constant.LIMIT_TAB_CHAT_AI) {
        return true;
    }
    const user = await getUserById(null, userDetail.id);
    const tabsLength =
        Utils.convertJSONDataToObject(user?.workSpace)?.[projectInuseDetail.id]?.find(
            i => i?.type === Constant.PANEL_TYPE.OPEN_CHAT_AI
        )?.tabList?.length || 0;

    return tabsLength >= Constant.LIMIT_TAB_CHAT_AI;
};

/**
 * Get user list by id list
 * @param {*} APIClient
 * @param {*} idList
 * @returns userDetailList
 */
export const getUserListByIdList = async (APIClient, idList) => {
    if (!idList || Utils.isEmptyArray(idList)) {
        return [];
    }
    idList = [...new Set(idList)];

    try {
        const queryToUserTableWithBatchIdDto = {
            query: getUserList,
            variables: {
                params: {
                    ids: idList,
                    method: Constant.GET_USER_LIST_METHOD.GET_USER_LIST_BY_ID_LIST
                }
            }
        };

        let resUserList = await API.graphql(queryToUserTableWithBatchIdDto);
        if (!Utils.isEmptyArray(resUserList?.data?.getUserList)) {
            return resUserList?.data?.getUserList;
        }
        return [];
    } catch (err) {
        Logger.error('getUserListByIdList error: ', err);
        return [];
    }
};


export const getListUserAddToProject = async (APIClient, corporationId, projectDetail) => {
    try {
        if (
            Utils.isEmptyObject(projectDetail) ||
            projectDetail.type === Constant.PROJECT_TYPE.BASE
        ) {
            return [];
        }

        let listUser = [];
        let corpUserList = [];

        let getAllUserInCorpParams = DynamoDBUserRequestDto.prepareParamsGetAllUserActiveInCorporation(corporationId);
        let resGetAllUserInCorp = await API.graphql({
            query: queries.getUserList,
            variables: {
                params: {
                    expression: JSON.stringify(getAllUserInCorpParams)
                }
            }
        });

        if (Utils.isEmptyArray(resGetAllUserInCorp?.data?.getUserList)) {
            return corpUserList;
        }
        corpUserList = resGetAllUserInCorp.data.getUserList;

        let getAllUserInProjectParams = DynamoDBProjectRequestDto.prepareParamsGetUserListInProject(projectDetail.id);
        let resGetAllUserInProject = await API.graphql({
            query: queries.getProjectList,
            variables: {
                params: {
                    expression: JSON.stringify(getAllUserInProjectParams)
                }
            }
        });
        let currentProjectUserList = !Utils.isEmptyArray(resGetAllUserInProject?.data?.getProjectList) ? resGetAllUserInProject?.data?.getProjectList : [];

        // Filter and some to get user not belong to current prj
        listUser = corpUserList.filter((corpUser) => {
            // If is general prj, only general user can add to prj
            if (!projectDetail.isGuest) {
                return (
                    !currentProjectUserList.some(
                        (projectUser) => corpUser.id === projectUser.id2
                    ) && Utils.isGeneralUserWithoutGuest(corpUser)
                );
            } else {
                return !currentProjectUserList.some(
                    (projectUser) => corpUser.id === projectUser.id2
                );
            }
        });

        listUser.forEach(async(user) => {
            // get avatar
            let avatar = Constant.AVATAR_DEFAULT;
            if (!Utils.isEmptyString(user.image)) {
                avatar = await Storage.get(user.image);
            }
            user.avatar = avatar;
        })

        return listUser;
    } catch (err) {
        console.log('getUserDetail error:', err);
    }
};

/**
 * Get user detail by userId and corporationId
 * @param {*} APIClient
 * @param {*} username
 * @param {*} corporationId
 * @returns
 */
export const getUserByUserIdAndCorporationId = async(APIClient, username, corporationId) => {
    try {
        const res = await API.graphql({
            query: queries.getUserList,
            variables: {
                params: {
                    expression: DynamoDBUserRequestDto
                        .getExpressionUserInCorporationByEmail(corporationId, username)
                }
            }
        });

        let user = null;
        if (!Utils.isEmptyArray(res.data?.getUserList)) {
            user = res.data.getUserList[0];
        }

        return user;
    } catch (error) {
        console.log('getUserByUserIdAndCorporationId error: ', error);
        return null;
    }
}

/**
 * edit user profile
 *
 * @param {*} APIClient
 * @param {*} userEdit
 * @returns
 */
export const editUserProfile = async(APIClient, userEdit) => {
    try {
        let userResult = await API.graphql({
            query: editUserInfo,
            variables: {
                editUserInfoRequestDto: {
                    id: userEdit.id,
                    userId: userEdit.userId,
                    firstName: userEdit.firstName,
                    lastName: userEdit.lastName,
                    image: userEdit.image,
                    tel: userEdit.tel,
                    mainLanguage: userEdit.mainLanguage,
                },
            },
        });
        
        return userResult?.data?.editUserInfo;
            
    } catch (error) {
        console.log('editUserProfile error:', error);
        return null;
    }
}

/**
 * Create user in project
 *
 * @param {*} APIClient
 * @param {*} requestBody
 * @returns data create
 */
export const createUserProject = async(APIClient, requestBody) => {
    try {
        const createUserProjectResult = await API.graphql({
                query: createProject,
                variables: {
                    input: {
                        usage: Constant.SYSTEM_USAGE_TYPE.PROJECT,
                        affiliationOrganization: requestBody.affiliationOrganization,
                        active: requestBody.active,
                        type: requestBody.type,
                        privilege: requestBody.privilege,
                        ...requestBody,
                    },
                },
            });
            return createUserProjectResult?.data?.createProject;
    } catch (error) {
        console.log('createUserProject error:', error);
        return null;
    }
}

export const updateUserDetail = async (APIClient, userId, userDetail) => {
    try {
        let input = {
            id: userId,
            ...userDetail,
        };
        let res = await API.graphql({ query: updateUser, variables: { input: input } });
        return res.data?.updateUser;
    } catch (err) {
        console.log(err);
        return null;
    }
};

/**
 *
 * @param {*} APIClient
 * @param {*} userId
 * @param {*} corporationDomain
 * @returns
 */
export const isExistUserInCorp = async (APIClient, userId, corporationDomain) => {
    try {
        const corporationInfo = await getCorporationByCorporationId(API, corporationDomain);
        if (Utils.isEmptyObject(corporationInfo)) {
            return false;
        }

        const corporationId = corporationInfo.id;
        const userDetail = await getUserByUserIdAndCorporationId(API, userId, corporationId);
        Logger.info('isExistUserInCorp: contentDetail', userDetail);

        return !Utils.isEmptyObject(userDetail);
    } catch (err) {
        Logger.error('isExistUserInCorp: err ', err);
        return false;
    }
};

/**
 * getUserListInProjectByProjectId
 * get user list in prj 
 * @param {*} APIClient 
 * @param {*} projectId 
 * @returns 
 */
export const getUserListInProjectByProjectId = async (APIClient, projectId) => {
    let userList = [];

    try {
        let dynamoDBParams = DynamoDBProjectRequestDto.prepareParamsGetUserListInProject(projectId);
        let resData = await API.graphql({
            query: queries.getProjectList,
            variables: {
                params: {
                    expression: JSON.stringify(dynamoDBParams)
                }
            }
        });

        let userProjectData = resData?.data?.getProjectList || [];
        if (Utils.isEmptyArray(userProjectData)) {
            return userList;
        }

        let userIdList = [];
        userProjectData.forEach(userProject => {
            if (userProject.id2) {
                userIdList.push(userProject.id2);
            }
        });

        let allUserDetailList = await getUserListByIdList(APIClient, userIdList);
        userList = await Promise.all(
            userProjectData.map(async (userProject) => {
                if (userProject.id2) {
                    let originalUser = allUserDetailList.find(user => user.id === userProject.id2);

                    // Protect against undefined user references
                    if (!originalUser) {
                        return null;
                    }

                    let isProjectManager =
                        userProject.privilege === Constant.PRIVILEGE_PROJECT.PRJ_MGR;
                    let isProjectMember = !isProjectManager;
                    let permission = Constant.PROJECT_PERMISSION.BASE;
                    if (isProjectManager) {
                        permission = Constant.PROJECT_PERMISSION.MANAGER;
                    } else if (isProjectMember) {
                        permission = Constant.PROJECT_PERMISSION.MEMBER;
                    }

                    // Clone user object to avoid direct mutation and safely assign projectDetail
                    let currentUser = {
                        ...originalUser,
                        projectDetail: {
                            active: userProject.active,
                            isProjectManager: isProjectManager,
                            isProjectMember: isProjectMember,
                            permission: permission,
                            defaultPermission: permission
                        },
                        userProjectId: userProject.id,
                        projectCreatedAt: userProject.createdAt
                    };

                    // Get avatar, safely handle image
                    let avatar = Constant.AVATAR_DEFAULT;
                    if (!Utils.isEmptyString(originalUser.image)) {
                        avatar = await Storage.get(originalUser.image);
                    }
                    currentUser.avatar = avatar;

                    return currentUser;
                }
            })
        );
        userList = userList
            .filter(user => !!user)
            .sort((a, b) => new Date(a.projectCreatedAt) - new Date(b.projectCreatedAt));

        return userList;
    } catch (err) {
        Logger.error('getuserListByUserNo error: Exception - ', err);
        return userList;
    }
};


/**
 * getGeneralUserInProjectByProjectId
 * get user list in prj 
 * @param {*} APIClient 
 * @param {*} projectId 
 * @returns 
 */
export const getGeneralUserInProjectByProjectId = async (APIClient, projectId) => {
    let userList = [];

    try {
        let dynamoDBParams = DynamoDBProjectRequestDto.prepareParamsGetUserGeneralInProject(projectId);
        let resData = await API.graphql({
            query: queries.getProjectList,
            variables: {
                params: {
                    expression: JSON.stringify(dynamoDBParams)
                }
            }
        });

        let userProjectData = resData?.data?.getProjectList || [];
        if (Utils.isEmptyArray(userProjectData)) {
            return userList;
        }

        await Promise.all(
            userProjectData.map(async (userProject) => {
                if (userProject.id2) {
                    let currentUser = await getUserById(API, userProject.id2);
                    if (currentUser && currentUser.active !== Constant.ACTIVE_FLAG.DELETED) {
                        let avatar = Constant.AVATAR_DEFAULT;
                        if (!Utils.isEmptyString(currentUser.image)) {
                            avatar = await Storage.get(currentUser.image);
                        }
                        currentUser.avatar = avatar;
    
                        userList.push(currentUser);
                    }
                }
            })
        );

        return userList;
    } catch (err) {
        Logger.error('getGeneralUserInProjectByProjectId error: Exception - ', err);
        return userList;
    }
};

const prepareUserInputBeforeUpdateUser = (userInput) => {
    let workSpace = userInput.workSpace;

    if (workSpace) {
        workSpace = Utils.convertJSONDataToObject(workSpace);
        if (!Utils.isEmptyObject(workSpace)) {
            const newWorkspace = {};

            for (const [prjId, panelList] of Object.entries(workSpace)) {
                const newPanelList = panelList.map(panel => {
                    if (panel.type === Constant.PANEL_TYPE.OPEN_CHAT_AI) {
                        const tabList = (panel.tabList || []).map(tab => {
                            const newTab = { ...tab };
                            if (newTab.filterKnowledgeListData) {
                                newTab.chatThreadId = newTab.filterKnowledgeListData.chatThreadId || newTab.filterKnowledgeListData.threadId;
                                
                                delete newTab.filterKnowledgeListData;
                            }
                            if (typeof newTab.tabNameChatAI === 'string') {
                                newTab.tabNameChatAI = newTab.tabNameChatAI.slice(0, 50);
                            }
                            return newTab;
                        });

                        return {
                            ...panel,
                            tabList
                        };
                    }

                    return panel;
                });

                newWorkspace[prjId] = newPanelList;
            }

            userInput.workSpace = JSON.stringify(newWorkspace);
        }
    }

    return userInput;
};

/**
 * Update user by id
 *
 * @param {*} APIClient
 * @param {*} userInput
 * @returns dataUpdated
 */
export const updateUserById = async (APIClient, userInput) => {
    try {
        userInput = prepareUserInputBeforeUpdateUser(userInput);
        let resData = await API.graphql({
            query: updateUser,
            variables: {
                input: userInput,
            },
            authMode: Constant.AUTH_MODE_API_KEY,
        });

        const updatedUser = resData.data.updateUser;
        return updatedUser;
    } catch (err) {
        console.log('updateUserById error 2: ', err);
        return null;
    }
};

/**
 * Update update UserSettingById (adminMode/panelSizeSetting) by id
 *
 * @param {*} userDetail
 * @param {*} panelList
 * @param {*} selectedProjectId
 * @param {*} updatePanelData
 * @param {*} selectedPanelSize
 * {
 *  adminMode: Boolean
 *  panelSizeSetting: AWSJSON
 * }
 * @returns dataUpdated
 */
export const updateUserSettingById = async (userDetail, setUserDetail, panelList, selectedProjectId,  updatePanelData) => {
    try {
        const currentUserSetting = Utils.convertJSONDataToObject(userDetail.userSetting);
        if (!currentUserSetting) return;
        const adminMode = currentUserSetting.adminMode;
        const currentPanelSizeSetting = {...currentUserSetting.panelSizeSetting};

        const oldSelectedProjectSizeSetting = currentPanelSizeSetting[selectedProjectId];

        const updatedPanelList = panelList.map(panel => {
            if (panel.type === updatePanelData.type) {
                return {
                    id: updatePanelData.id,
                    type: updatePanelData.type,
                    size: updatePanelData.size
                };
            }
            return {
                id: panel.id,
                type: panel.type,
                size: panel.size || Constant.PANEL_SIZE.SIZE_1
            };
        });

        const comparePanelSetting = oldSelectedProjectSizeSetting && !Utils.isEmptyArray(oldSelectedProjectSizeSetting)
            ? oldSelectedProjectSizeSetting.map(oldPanel => {
                  const newPanel = updatedPanelList.find(panel => panel.type === oldPanel.type);
                  if (newPanel) {
                      return {
                          id: newPanel.id,
                          type: newPanel.type,
                          size: newPanel.size
                      };
                  }
                  return oldPanel;
              })
            : [...updatedPanelList];

        // Add any new panels from updatedPanelList that are not in oldSelectedProjectSizeSetting
        updatedPanelList.forEach(newPanel => {
            const exists = comparePanelSetting.find(panel => panel.type === newPanel.type);
            if (!exists) {
                comparePanelSetting.push(newPanel);
            }
        });

        // Spread the current setting first: this bag also carries keys this
        // function knows nothing about (e.g. mainLanguage for auto-translation),
        // and enumerating only the two known keys would silently drop them.
        const userSetting = JSON.stringify({
            ...currentUserSetting,
            adminMode: adminMode,
            panelSizeSetting: {
                ...currentPanelSizeSetting,
                [selectedProjectId]: [...comparePanelSetting]
            }
        })


        let updateInput = {
            id: userDetail.id,
            userSetting: userSetting // AWSJSON
        }

        const resData = await API.graphql({
            query: updateUser,
            variables: {
                input: updateInput,
            },
            authMode: Constant.AUTH_MODE_API_KEY,
        });

        if (!resData) return null;

        setUserDetail(prev => {
            return ({
            ...prev,
            userSetting: resData.data.updateUser.userSetting
        })}) // update userSetting to userDetail atom 

        return resData.data.updateUser;
    } catch (error) {
        console.log('updateUserSettingById error: ', error);
        return null;
    }
}

/**
 * 
 * @param {*} APIClient 
 * @param {*} userInput 
 * @returns 
 */
export const updateUserResource = async (APIClient, userInput) => {
    try { 
        let resData = await API.graphql({
            query: editUserResource,
            variables: {
                editUserResourceRequestDto: userInput,
            },
        });
        return resData.data.editUserResource;
    } catch (err) {
        console.log('updateUserResource error: ', err);
        return null;
    }
};

export const deleteCookieCdn = async () => {
    fetch(`${process.env.REACT_APP_CLOUD_FRONT_URL}/deleteCookie`, {
        mode: 'no-cors',
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
        }).then((response) => response.json())
        .catch((error) => console.log('error :>> ', error));
};


// func call cloudfront to create access s3 cookie
const createAccessFileCookieApi = (requestBody, requestBodyObj) => {
    pushClientLog('', {
        ...requestBodyObj,
        action: Constant.PUSH_LOG_CLIENT_ACTION.CREATE_COOKIE,
        loginKey: `${requestBodyObj.userId}_${requestBodyObj.corporationId}_${requestBodyObj.email}`
    })

    fetch(`${process.env.REACT_APP_CLOUD_FRONT_URL}/createCookies`, {
        mode: 'no-cors',
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        }).then((response) => {
            const callbackLoginUrl = sessionStorage.getItem('callbackLoginUrl');
            if (callbackLoginUrl) {
                sessionStorage.removeItem('callbackLoginUrl');
                window.location.href = callbackLoginUrl;
            }
            return Utils.convertJSONDataToObject(response);
        })
        .catch((error) => console.log('error :>> ', error));
};

// function create cookie access file s3
export const createCookieAccessFileForUser = async (userDetail, userInfoSession) => {
    return Auth.currentSession()
        .then(async (data) => {
          
            //list project of user (member and project manager)
            const projectList = userInfoSession.projectList;
            
            //get list project of current cookie (save in local storage);
            let listProjectInCookies = [];
            if (localStorageUtil.getListProjectOfUser()) {
                listProjectInCookies = localStorageUtil.getListProjectOfUser();
            }
            
            const requestBodyObj = {
                corporationId: userDetail?.affiliationOrganization ? userDetail?.affiliationOrganization : '',
                projectInuseId: userInfoSession.projectId || localStorageUtil.getSelectedProjectId(),
                projectList: projectList,
                userId: userDetail.id,
                email: userDetail.mail,
                accessToken: data.getAccessToken().jwtToken,
                refreshToken: data.getRefreshToken().token
            };

            const requestBodyData = crypto.AES.encrypt(
                JSON.stringify(requestBodyObj),
                Constant.S3_URL_AES_ENCODE_KEY
            ).toString();

            // compare the lengths of two arrays
            if (
                Utils.isEmptyArray(listProjectInCookies) ||
                projectList.length !== listProjectInCookies.length
            ) {
                localStorageUtil.setListProjectOfUser(JSON.stringify(projectList));
                return createAccessFileCookieApi({
                    data: requestBodyData,
                }, requestBodyObj);
                
            }

            //compares items in two arrays when the lengths of the two arrays are equal
            for (let projectId of projectList) {
                
                //call function create cookie
                if (!listProjectInCookies.includes(projectId)) {
                    localStorageUtil.setListProjectOfUser(JSON.stringify(projectList));
                    return createAccessFileCookieApi({
                        data: requestBodyData,
                    }, requestBodyObj);
                }
            }
        })
        .catch((error) => console.log('error :>> ', error));
};

// prepare data to update session user info
export const prepareUserInfoSessionData = async (
    APIClient,
    userDetail,
    projectInuseDetail,
    corporationDetail,
    projectUserListIncorproration
) => {
    try {
        let listProjectId = [];
        let userId = userDetail?.id ? userDetail.id : localStorageUtil.getUserDetailId();
        let corporationId = corporationDetail?.id ? corporationDetail.id : localStorageUtil.getAffiliationOrganization();

        if (!Utils.isEmptyArray(projectUserListIncorproration)) {
            projectUserListIncorproration.forEach((project) => listProjectId.push(project.id));
        } else {
            listProjectId = await getProjectIdListUserIsMember(corporationId, userId);
        }

        let userInfoTemp = sessionStorageUtil.getUserInfoSession();
        if (Utils.isEmptyObject(userInfoTemp)) {
            userInfoTemp = {};
        }

        userInfoTemp.userId = userId;
        userInfoTemp.projectList = listProjectId;
        if (!Utils.isEmptyObject(userDetail)) {
            userInfoTemp.type = userDetail.type;
            userInfoTemp.isAdmin = userDetail.privilege === Constant.PRIVILEGE_USER.ADMIN || userDetail.privilege === Constant.PRIVILEGE_USER.ADMIN_OWNER;
        }
        if (!Utils.isEmptyObject(projectInuseDetail)) {
            userInfoTemp.projectId = projectInuseDetail.id;
            userInfoTemp.projectType = projectInuseDetail.type;
            userInfoTemp.projectDesk = projectInuseDetail.isDesk;
            userInfoTemp.isProjectManager = projectInuseDetail.isProjectManager;
        }

        userInfoTemp.orgId = corporationId;

        return userInfoTemp;
    } catch (err) {
        console.log('prepareUserInfoSessionData :>> ', err);
    }
};

/**
 * default infor for deleted-user
 * @param {*} id 
 * @returns 
 */
export const getUserDeletedInfor = (id, affiliationProject) => {
    let avatar = Constant.AVATAR_DEFAULT;
    return {
        id: id, mail: 'Deleted User',
        firstName: "{\"jp\":{\"A\":\"F\",\"text\":\"Deleted User\"}}",
        lastName: "",
        avatar,
        deleteFlag: 1,
        affiliationProject: affiliationProject
    };
};

/**
 * getListCustomerUserCanAddToProject
 * @param {*} APIClient 
 * @param {*} corporationId 
 * @param {*} projectDetail 
 * @returns 
 */
export const getListCustomerUserCanAddToProject = async (APIClient, corporationId, projectDetail) => {
    try {
        if (!Utils.isPublicDesk(projectDetail)) {
            return [];
        }

        let customerUserList = [];
        let corpCustomerUserList = [];

        corpCustomerUserList = await getListCustomerUserWithoutDeletedInCorporation(API, corporationId);
        if (Utils.isEmptyArray(corpCustomerUserList)) {
            return [];
        }
        let currentProjectCustomerUserList = await getListCustomerUserInProject(API, projectDetail.id);

        // Filter and some to get user not belong to current prj
        customerUserList = corpCustomerUserList.filter((corpUser) => {
            return !currentProjectCustomerUserList.some(
                (projectUser) => corpUser.id === projectUser.id2
            );
        });

        return customerUserList;
    } catch (err) {
        console.log('getListCustomerUserCanAddToProject error:', err);
        return [];
    }
};


/**
 * getListCustomerUserWithoutDeletedInCorporation
 * get all customer in corporation
 * @param {*} APIClient 
 * @param {*} corporationId 
 * @returns 
 */
export const getListCustomerUserWithoutDeletedInCorporation = async (APIClient, corporationId) => {
    try {
        let customerList = [];
        let getAllCustomerInCorpParams = DynamoDBUserRequestDto.prepareParamsGetAllCustomerWithoutDeletedInCorporation(corporationId);
        let resGetAllCustomerUserInCorp = await API.graphql({
            query: queries.getUserList,
            variables: {
                params: {
                    expression: JSON.stringify(getAllCustomerInCorpParams)
                }
            }
        });
    
        if (!Utils.isEmptyArray(resGetAllCustomerUserInCorp?.data?.getUserList)) {
            customerList = resGetAllCustomerUserInCorp?.data?.getUserList;
        }
        return customerList;
    } catch (error) {
        console.log('getListCustomerUserWithoutDeletedInCorporation error', error);
        return [];
    }
}


/**
 * getListCustomerUserInProject
 * get all customer in project
 * @param {*} APIClient 
 * @param {*} projectId 
 * @returns 
 */
export const getListCustomerUserInProject = async (APIClient, projectId) => {
    try {
        let customerList = [];
        let getAllCustomerInProjectParams = DynamoDBProjectRequestDto.prepareParamsGetCustomerUserListInProject(projectId);
        let resGetAllCustomerUserInProject = await API.graphql({
            query: queries.getProjectList,
            variables: {
                params: {
                    expression: JSON.stringify(getAllCustomerInProjectParams)
                }
            },
        });

        if (!Utils.isEmptyArray(resGetAllCustomerUserInProject?.data?.getProjectList)) {
            customerList = resGetAllCustomerUserInProject?.data?.getProjectList;
        }
        return customerList;
    } catch (error) {
        console.log('getListCustomerUserInProject error', error);
        return [];
    }
}


/**
 * addCustomerUserToCurrentProject
 * @param {*} APIClient 
 * @param {*} customerUserList 
 */
export const addCustomerUserToCurrentProject = async (APIClient, corporationId, projectId, currentUserId, customerUserList) => {
    if (!corporationId || !projectId || !currentUserId || Utils.isEmptyArray(customerUserList)) {
        return { status: false };
    }

    const addCustomerUserRequestDto = {
        currentUserId,
        corporationId,
        projectId,
        customerList: customerUserList
    };

    try {
        const addCustomerUserResponse = await API.graphql({
            query: addCustomerUserToProject,
            variables: {
                addCustomerUserToProjectRequestDto: addCustomerUserRequestDto
            },
        });

        return addCustomerUserResponse?.data?.addCustomerUserToProject;
    } catch (err) {
        console.error('addCustomerUserResponse error:', err);
        return { status: false, error: err instanceof Error ? err.message : err };
    }
};

/**
 * Get user customer list in project
 * @param {*} APIClient 
 * @param {*} projectId 
 * @returns 
 */
export const getCustomerListInProjectByProjectId = async (APIClient, projectId) => {
    let userList = [];

    try {
        let userProjectData = await getListCustomerUserInProject(API, projectId);
        
        userList = await Promise.all(
            userProjectData.map(async (userProject) => {
                if (userProject.id2) {
                    let currentUser = await getUserById(API, userProject.id2);
                    // user id in project table
                    currentUser.userProjectId = userProject.id;

                    // get avatar
                    let avatar = '/assets/image/icon-user-default.svg';
                    if (!Utils.isEmptyString(currentUser.image)) {
                        avatar = await Storage.get(currentUser.image);
                    }
                    currentUser.avatar = avatar;
                    currentUser.activeInProject = userProject.active;
                    currentUser.idInProject = userProject.id;

                    return currentUser;
                }
            })
        );

        userList = Utils.sortByCreatedAtDesc(userList);

        return userList;
    } catch (err) {
        Logger.error('getuserListByUserNo error: Exception - ', err);
        return userList;
    }
};


 export const submitEmailForgotPassword = async (APIClient, requestBody) => {
    const submitEmailForgotPasswordResponse = await API.graphql({
        query: forgotPassword,
        variables: {
            forgotPasswordRequestDto: {
                ...requestBody
            }
        },
        authMode: Constant.AUTH_MODE_API_KEY
    });
    return submitEmailForgotPasswordResponse?.data?.forgotPassword
};


/**
 * getListAdminOfCorporation
 * @param {*} APIClient 
 * @param {*} corporationId 
 * @returns 
 */
export const getListAdminOfCorporation = async (APIClient, corporationId) => {
    try {
        let res = await API.graphql({
            query: queries.getUserList,
            variables: {
                params: {
                    expression: JSON.stringify(DynamoDBUserRequestDto.preparePramsGetAllAdminOfCorporation(corporationId))
                }
            },
        });

        return !Utils.isEmptyArray(res.data?.getUserList) ? res.data?.getUserList : [];
    } catch (err) {
        console.log('getListAdminOfCorporation :>> ', err);
        return [];
    } 
}

export const getAllUserInBaseProject = async (APIClient, corporationId) => {
    try {
        const expression = DynamoDBUserRequestDto.prepareParamsGetAllUserInBaseUser(
            corporationId
        );
        let res = await API.graphql({
            query: queries.getUserList,
            variables: {
                params: {
                    expression: JSON.stringify(expression)
                }
            }
        });

        return !Utils.isEmptyArray(res.data?.getUserList) ? res.data?.getUserList : [];
    } catch (error) {
        console.log('getAllUserInBaseProject error :>> ', error);
        return [];
    }
};


/**
 * handleRestoreUser
 * @param {*} APIClient 
 * @param {*} requestDto 
 * @returns 
 */
export const handleRestoreUser = async (APIClient, requestDto) => {
    try {
        const result = await API.graphql({
            query: restoreUser,
            variables: {
                restoreUserRequestDto: requestDto,
            },
        });

        return result.data.restoreUser;
    } catch (err) {
        Logger.error('handleRestoreUser', err);
        return null;
    }
};


/**
 * getUserListInCorporationInUserManagement
 * @param {*} APIClient 
 * @param {*} corporationId 
 * @returns 
 */
export const getUserListInCorporationInUserManagement = async (APIClient, corporationId) => {
    if (!corporationId) return [];
    try {

        // query params
        let userList = [];
        let resGetAllUserInCorp = {};

        try {
            resGetAllUserInCorp = await API.graphql({
                query: queries.getUserList,
                variables: {
                    params: {
                        method: Constant.GET_USER_LIST_METHOD.GET_USER_LIST_IN_USER_MANAGEMENT,
                        corporationId: corporationId
                    }
                }
            });
        } catch (error) {
            console.log('getUserListByCorporation', error);
        }
        
        if (Utils.isEmptyArray(resGetAllUserInCorp?.data?.getUserList)) {
            return userList;
        }
        userList = resGetAllUserInCorp.data.getUserList;
        return userList;
    } catch (err) {
        console.log('getUserListByCorporation error: ', err);
        return [];
    }
};